import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Linkedin, Twitter, Instagram, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CALENDLY_URL = 'https://calendly.com/contact-bankkimedia/30min';

const philosophyImages = [
  'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/sc1e2vgd_Screenshot%202026-02-22%20125403.png',
  'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/6st1ltyn_Screenshot%202026-02-22%20125526.png',
  'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/1ke6c65f_Screenshot%202026-02-22%20125517.png',
];
const alignments = ['left', 'right', 'left'];

const TeamCard = ({ member, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: 0.3 });
  return (
    <motion.div ref={cardRef} initial={{ opacity: 0, y: 60 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }} className="group" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl mb-6">
        <motion.img src="https://customer-assets.emergentagent.com/job_4c9ec105-7961-43e0-9222-2e0dd1f1cf63/artifacts/jkz19tbm_Screenshot%202026-02-22%20134510.png" alt={member.name} className="absolute inset-0 w-full h-full object-cover" animate={{ scale: isHovered ? 1.1 : 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} />
        <motion.div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" animate={{ opacity: isHovered ? 0.9 : 0.6 }} />
        <motion.div className="absolute bottom-6 left-6 right-6 flex gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 20 }} transition={{ duration: 0.3 }}>
          {[Linkedin, Twitter, Instagram].map((Icon, i) => <motion.a key={i} href="#" whileHover={{ scale: 1.1, y: -2 }} className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-[#c8ff00] transition-colors"><Icon className="w-4 h-4 text-black" /></motion.a>)}
        </motion.div>
      </div>
      <div className="space-y-2 text-center">
        <h3 className="text-2xl md:text-3xl font-bold font-['Syne']">{member.name}</h3>
        <p className="text-[#c8ff00] font-medium">{member.role}</p>
        <p className="text-white/50 text-sm leading-relaxed">{member.bio}</p>
      </div>
    </motion.div>
  );
};

const CinematicSection = ({ section, image, alignment, index }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.2, 1, 1.1]);
  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center py-24 md:py-32 overflow-hidden">
      <motion.div className="absolute inset-0 z-0" style={{ scale: imageScale }}>
        <img src={image} alt={section.title} className="w-full h-full object-cover" />
        <div className={`absolute inset-0 ${alignment === 'left' ? 'bg-gradient-to-r from-black via-black/80 to-transparent' : 'bg-gradient-to-l from-black via-black/80 to-transparent'}`} />
        <div className="absolute inset-0 bg-black/30" />
      </motion.div>
      <div className="container-custom relative z-10">
        <motion.div className={`max-w-2xl ${alignment === 'right' ? 'ml-auto text-right' : ''}`} style={{ y }}>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="text-[#c8ff00] text-sm uppercase tracking-[0.3em] mb-4">{section.subtitle}</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-['Syne'] leading-[1.1] mb-6">{section.title}</motion.h2>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.5 }} className="text-white/70 text-lg md:text-xl leading-relaxed">{section.description}</motion.p>
        </motion.div>
      </div>
    </section>
  );
};

const AboutPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const { t } = useTranslation();
  const a = t('about', { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main>
        <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
          <motion.div style={{ y }} className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black z-10" />
            <img src="https://customer-assets.emergentagent.com/job_4c9ec105-7961-43e0-9222-2e0dd1f1cf63/artifacts/rx35mjzc_Screenshot%202026-02-22%20134426.png" alt="Bankki Media" className="absolute inset-0 w-full h-full object-cover" />
          </motion.div>
          <motion.div style={{ opacity }} className="relative z-20 text-center px-8">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={isHeroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.2 }} className="text-white/60 text-sm uppercase tracking-[0.3em] mb-6">{a.label}</motion.p>
            <div className="overflow-hidden"><motion.h1 initial={{ y: 120 }} animate={isHeroInView ? { y: 0 } : {}} transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold font-['Syne'] tracking-tight leading-[0.9]">{a.h1}</motion.h1></div>
            <div className="overflow-hidden"><motion.h1 initial={{ y: 120 }} animate={isHeroInView ? { y: 0 } : {}} transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }} className="text-5xl md:text-7xl lg:text-8xl xl:text-9xl font-bold font-['Syne'] tracking-tight leading-[0.9] text-[#c8ff00]">{a.h1Accent}</motion.h1></div>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={isHeroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: 0.8 }} className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mt-8">{a.desc}</motion.p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity }} className="flex flex-col items-center gap-2 text-white/50">
              <span className="text-xs uppercase tracking-widest">Scroll</span>
              <div className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent" />
            </motion.div>
          </motion.div>
        </section>

        <section className="py-24 bg-gradient-to-b from-[#0a0a0a] to-gray-900/50 border-b border-white/10">
          <div className="container-custom">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {a.stats.map((stat, index) => (
                <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }} className="text-center">
                  <p className="text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] text-[#c8ff00]">{stat.number}</p>
                  <p className="text-white/50 text-sm uppercase tracking-wider mt-2">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-32 md:py-48 relative overflow-hidden bg-[#0a0a0a]">
          <div className="absolute inset-0 opacity-5"><div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} /></div>
          <div className="container-custom relative z-10">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-5xl mx-auto text-center">
              <p className="text-white/50 text-sm uppercase tracking-[0.3em] mb-8">{a.philosophy.label}</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-['Syne'] leading-tight">
                {a.philosophy.quote1}{' '}<span className="text-[#c8ff00]">{a.philosophy.accent1}</span>{' '}{a.philosophy.mid}{' '}<span className="text-[#c8ff00]">{a.philosophy.accent2}</span>{' '}{a.philosophy.quote2}
              </h2>
              <p className="text-white/60 text-xl mt-8 max-w-3xl mx-auto leading-relaxed">{a.philosophy.desc}</p>
            </motion.div>
          </div>
        </section>

        {a.sections.map((section, index) => <CinematicSection key={index} section={section} image={philosophyImages[index]} alignment={alignments[index]} index={index} />)}

        <section className="py-24 md:py-32 bg-[#0a0a0a]">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
              <p className="text-white/50 text-sm uppercase tracking-[0.3em] mb-4">{a.team.label}</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne']">{a.team.title}{' '}<span className="text-[#c8ff00]">{a.team.accent}</span></h2>
            </motion.div>
            <div className="max-w-sm mx-auto">
              <TeamCard member={{ name: a.team.name, role: a.team.role, bio: a.team.bio }} index={0} />
            </div>
          </div>
        </section>

        <section className="relative py-32 md:py-48 overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://customer-assets.emergentagent.com/job_4c9ec105-7961-43e0-9222-2e0dd1f1cf63/artifacts/hcp2bn4y_Screenshot%202026-02-22%20134730.png" alt="Get started" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/50" />
          </div>
          <div className="container-custom relative z-10">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center max-w-4xl mx-auto">
              <p className="text-[#c8ff00] text-sm uppercase tracking-[0.3em] mb-6">{a.cta.label}</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] mb-8">{a.cta.title}{' '}<span className="text-[#c8ff00]">{a.cta.accent}</span>.</h2>
              <p className="text-white/70 text-lg md:text-xl mb-10 max-w-2xl mx-auto">{a.cta.desc}</p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="about-cta" className="inline-flex items-center gap-3 px-10 py-5 bg-[#c8ff00] text-black font-bold text-lg rounded-full hover:bg-[#d4ff33] transition-all duration-300">
                  {a.cta.button}<ArrowRight className="w-5 h-5" />
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
